import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ReloadOutlined } from "@ant-design/icons";
import { App, Card, ConfigProvider, Modal, Skeleton } from "antd5";

import { Input, Radio, Select, TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Divider from "components/ui/divider/Divider";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { useGenerateOutreach } from "lib/hooks/api/outreach/useGenerateOutreach";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import Sparkles from "lib/icons/Sparkles";
import { BlueSkeleton } from "lib/themes/BlueSkeleton";
import { blue500, white } from "lib/themes/colors";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import {
  GenerateOutreachRequest,
  GenerateOutreachRequest_IntentEnum_0,
  GenerateOutreachRequest_LengthEnum_0,
  GenerateOutreachRequest_ToneEnum_0,
} from "../../lib/generated/app-service-gql/graphql";
import { OutreachResult } from "./OutreachResult";
import { useOutreachTracking } from "./tracking";

import css from "./OutreachBuilderModal.module.scss";

type Intent = GenerateOutreachRequest["intent"];
type Tone = GenerateOutreachRequest_ToneEnum_0;
type Length = GenerateOutreachRequest["length"];

type FormValues = {
  intent: Intent;
  companyInformation?: string;
  senderPersonalisation?: string;
  tone: Tone[];
  length: Length;
  buyerName: string;
  contactName: string;
  contactJobTitle: string;
  numberOfResponses: number;
};

const toneOptions = [
  {
    label: "🙂 Friendly",
    value: "Friendly",
  },
  { label: "😌 Relaxed", value: "Relaxed" },
  { label: "💪 Bold", value: "Bold" },
  { label: "💡 Witty", value: "Witty" },
  { label: "🧠 Knowledgeable", value: "Knowledgeable" },
  { label: "🤗 Empathetic", value: "Empathetic" },
  { label: "💼 Professional", value: "Professional" },
  { label: "😄 Fun", value: "Fun" },
  { label: "🏝️ Casual", value: "Casual" },
];

type Props = {
  buyerName: string;
  contactName: string;
  jobTitle: string;
  contactEmail: string;
  isOpen: boolean;
  onClose: () => void;
  senderPersonalisation?: string;
};

type Defaults = {
  intent: Intent;
  companyInformation?: string;
  senderPersonalisation?: string;
  tone: Tone[];
  length: Length;
};

export function getNoticePersonalisation(notice: NoticeDetails) {
  const supplierNames = notice.suppliers.map((s) => s.name).join(", ");
  const buyerName = notice.buyers[0]?.name ?? "";

  if (notice.expiryDate) {
    const expiryDate = new Date(notice.expiryDate);
    const isFuture = expiryDate.getTime() > Date.now();
    return `Mention the current contract ${buyerName} has with ${supplierNames} ${
      isFuture ? "is expiring" : "expired"
    }  ${toHumanRelativeDateFromNow(notice.expiryDate)}`;
  }

  return `Mention the current contract ${buyerName} has with ${supplierNames}`;
}

export function OutreachBuilderModal({
  contactName,
  buyerName,
  jobTitle,
  contactEmail,
  isOpen,
  onClose,
  senderPersonalisation,
}: Props) {
  const { message } = App.useApp();
  const [savedDefaults, setSavedDefaults] = useLocalStorage<Defaults>("outreachBuilderDefaults", {
    intent: GenerateOutreachRequest_IntentEnum_0.Awareness,
    tone: [GenerateOutreachRequest_ToneEnum_0.Casual],
    length: GenerateOutreachRequest_LengthEnum_0.Short,
  });
  const { track } = useOutreachTracking();
  const {
    control,
    handleSubmit,
    formState,
    reset: resetForm,
  } = useForm<FormValues>({
    defaultValues: {
      ...savedDefaults,
      buyerName,
      contactName,
      contactJobTitle: jobTitle,
      numberOfResponses: 1,
      senderPersonalisation: senderPersonalisation ?? savedDefaults.senderPersonalisation,
    },
  });

  const {
    isLoading,
    mutate,
    data,
    reset: resetApi,
  } = useGenerateOutreach({
    onError: () => message.error("Sorry something has gone wrong"),
  });

  useEffect(() => {
    resetForm((value) => ({
      ...value,
      buyerName,
      contactName,
      contactJobTitle: jobTitle,
      numberOfResponses: 1,
      senderPersonalisation: senderPersonalisation ?? value.senderPersonalisation,
    }));
    resetApi();
  }, [contactName, buyerName, jobTitle, senderPersonalisation, resetForm, resetApi]);

  const onSubmit = handleSubmit((data) => {
    track(formState.submitCount > 1 ? "regenerate" : "generate", data);
    mutate({ input: { ...data, numberOfResponses: Number(data.numberOfResponses) } });
    setSavedDefaults({
      intent: data.intent,
      tone: data.tone,
      length: data.length,
      companyInformation: data.companyInformation,
      senderPersonalisation: data.senderPersonalisation,
    });
  });

  return (
    <ConfigProvider theme={BlueSkeleton}>
      <Modal
        open={isOpen}
        title={null}
        footer={null}
        onCancel={onClose}
        className={css.modal}
        styles={{ body: { padding: 0 } }}
      >
        <form className={css.content} onSubmit={onSubmit} id="outreach">
          <div className={css.sidePanel}>
            <div className={css.outreachHeader}>
              <h2>Outreach builder</h2>
              <span className={css.subTitle}>
                Create personalised and engaging outreach messages in seconds.
              </span>
            </div>
            <div className={css.formContent}>
              <div className={css.intentContainer}>
                <h3>I want to...</h3>
                <Controller
                  name="intent"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <>
                      <Card
                        className={
                          field.value === "Awareness" ? css.intentCardSelected : css.intentCard
                        }
                        styles={{ body: { padding: 16 } }}
                        onClick={() => field.onChange("Awareness")}
                        role="button"
                        aria-label="Generate awareness"
                      >
                        <h3>Generate awareness</h3>
                        <span>
                          Engage with buyers early to build awareness around your company, products
                          and services.{" "}
                        </span>
                      </Card>
                      <Card
                        className={
                          field.value === "Meeting" ? css.intentCardSelected : css.intentCard
                        }
                        styles={{ body: { padding: 16 } }}
                        onClick={() => field.onChange("Meeting")}
                        role="button"
                        aria-label="Book a meeting"
                      >
                        <h3>Book a meeting</h3>
                        <span>
                          Lock in time to directly speak with a decision maker and solidify
                          relationships around an upcoming contract expiry{" "}
                        </span>
                      </Card>
                    </>
                  )}
                />
              </div>

              <TextArea
                label="About your products/services (optional)"
                name="companyInformation"
                control={control}
                subLabel="Share details about your company and/or services to create a more engaging message."
                rows={4}
                placeholder={`For example...
[Your company name] are a certified reseller of [insert product/service here]. We are on [Framework name]. `}
              />

              <TextArea
                label="Mention (optional)"
                name="senderPersonalisation"
                control={control}
                subLabel="Better engage with the contact by adding a personal note."
                rows={4}
              />

              <Select
                label="Tone of voice"
                name="tone"
                options={toneOptions}
                control={control}
                rules={{
                  validate: (value) => {
                    if (value && Array.isArray(value) && value.length > 0) {
                      return true;
                    }
                    return "Please select at least one tone of voice";
                  },
                }}
                mode="multiple"
                data-testid="tone-select"
              />

              <Radio
                label="Length"
                name="length"
                control={control}
                options={[
                  { value: "Short", label: "Short" },
                  { value: "Long", label: "Long" },
                ]}
              />
            </div>
          </div>
          <div className={css.dividerVertical} />

          {data ? (
            <div className={css.sidePanel}>
              <h2>Results:</h2>
              <Divider className={css.divider} />
              <div className={css.results}>
                {data.generateOutreach.responses.map(({ subject, body }, index) => (
                  <React.Fragment key={`${subject}-${index}`}>
                    <OutreachResult subject={subject} body={body} email={contactEmail} />
                    <Divider className={css.divider} />
                  </React.Fragment>
                ))}

                <div className={css.generateContainer}>
                  <div className={css.actionItems}>
                    <Button buttonType="secondary" type="submit">
                      <ReloadOutlined /> Refresh results
                    </Button>
                    <Input
                      name="numberOfResponses"
                      label=""
                      control={control}
                      type="number"
                      max={5}
                      min={1}
                      rules={{ min: 1, max: 5 }}
                      className={css.numberOfResults}
                    />
                  </div>
                  <Button buttonType="tertiary" onClick={() => track("skip")}>
                    <a href={`mailto:${contactEmail}`} target="_blank" rel="noreferrer">
                      Skip, reach out directly
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {isLoading ? (
                <div className={css.sidePanel}>
                  <h2>Results:</h2>
                  <Divider className={css.divider} />

                  <Skeleton title paragraph active />
                  <Skeleton paragraph active />
                  <Skeleton paragraph active />
                  <Skeleton paragraph active />
                </div>
              ) : (
                <div className={css.experimentContainer}>
                  <Sparkles className={css.largeIcon} fill={blue500} />
                  <span>Generate outreach messages and they will appear here.</span>
                  <div className={css.generateContainer}>
                    <Button type="submit" className={css.generateBtn}>
                      <Sparkles fill={white} className={css.icon} /> Generate
                    </Button>
                    <Input
                      name="numberOfResponses"
                      title="number of results"
                      label=""
                      control={control}
                      type="number"
                      max={5}
                      min={1}
                      rules={{ min: 1, max: 5 }}
                      className={css.numberOfResults}
                    />
                  </div>
                  <Button buttonType="tertiary" onClick={() => track("skip")}>
                    <a href={`mailto:${contactEmail}`} target="_blank" rel="noreferrer">
                      Skip, reach out directly
                    </a>
                  </Button>
                </div>
              )}
            </>
          )}
        </form>
      </Modal>
    </ConfigProvider>
  );
}
